<template>
  <div class="flex flex-col gap-6 proxima_nova text_color">
    <!-- Page heading -->
    <h1 class="text-xl font-semibold m-0">Error Logging</h1>

    <!-- Alerts section -->
    <div class="flex flex-row justify-between items-center">
      <h2 class="text-base font-semibold m-0">Alerts</h2>
      <a-button class="primary_medium" @click="openCreate">
        Create Alert
      </a-button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center py-12">
      <CustomLoader :size="24" />
    </div>

    <!-- Empty state -->
    <div v-else-if="alerts.length === 0" class="flex flex-col items-center justify-center py-12 gap-4">
      <p class="secondary_color text-sm">No alert integrations configured yet.</p>
      <a-button class="primary_medium" @click="openCreate">
        Create your first alert
      </a-button>
    </div>

    <!-- Table -->
    <SimpleTable
      v-else
      :headers="columns"
      :data="tableData"
      :height="'calc(100vh - 320px)'"
      :custom-row="(record) => ({ onClick: () => handleRowClick(record) })"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'label'">
          <span class="text-sm font-medium">{{ record.label || 'Untitled alert' }}</span>
        </template>
        <template v-else-if="column.key === 'enabled'">
          <StatusTagProvider
            source="alert_settings"
            :value="record.enabled ? 'active' : 'inactive'"
            :text="record.enabled ? 'Active' : 'Inactive'"
            :color="record.enabled ? 'green' : 'grey'"
          />
        </template>
        <template v-else-if="column.key === 'service_name'">
          <span class="text-sm">{{ record.service_name }}</span>
        </template>
        <template v-else-if="column.key === 'space_name'">
          <span class="text-sm">{{ record.space_name }}</span>
        </template>
        <template v-else-if="column.key === 'channel_name'">
          <span class="text-sm">{{ record.channel_name }}</span>
        </template>
        <template v-else-if="column.key === 'created_at'">
          <span class="text-sm secondary_color">{{ formatDate(record.created_at) }}</span>
        </template>
        <template v-else-if="column.key === 'actions'">
          <a-dropdown :trigger="['click']" @click.stop>
            <ThemeImage
              :icon="imageNames.MORE_VERTICAL"
              class="size-5 cursor-pointer"
              alt="actions"
            />
            <template #overlay>
              <a-menu @click="({ key }) => handleAction(key, record)">
                <a-menu-item key="edit">Edit</a-menu-item>
                <a-menu-item key="test">Test Alert</a-menu-item>
                <a-menu-item key="delete">
                  <span class="text-red-500">Delete</span>
                </a-menu-item>
              </a-menu>
            </template>
          </a-dropdown>
        </template>
      </template>
    </SimpleTable>

    <!-- Delete confirmation -->
    <ConfirmationModal
      v-model:visible="showDeleteModal"
      title="Delete Alert"
      message="Are you sure you want to delete this alert? This action cannot be undone."
      type="delete"
      confirmText="Delete"
      cancelText="Cancel"
      :loading="deleting"
      loadingText="Deleting..."
      @confirm="handleDelete"
      @cancel="showDeleteModal = false"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { message } from "ant-design-vue";

import ErrorTrackingService from "@/src/api/ErrorTrackingService.js";
import SpaceService from "@/src/api/SpaceService.js";
import ChatService from "@/src/api/ChatService.js";
import { spaceServiceTypes, spaceRelationTypes } from "@/src/constants/spaceConstants.js";
import SimpleTable from "@/src/components/custom_table_components/SimpleTable.vue";
import StatusTagProvider from "@/src/components/StatusTagProvider.vue";
import CustomLoader from "@/src/components/CustomLoader.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import ConfirmationModal from "@/src/components/ConfirmationModal.vue";
import { imageNames } from "@/src/constants/imageConstants.js";

const router = useRouter();

const loading = ref(false);
const alerts = ref([]);
const services = ref([]);
const spaces = ref([]);
const channelsMap = ref({});
const showDeleteModal = ref(false);
const deleting = ref(false);
const alertToDelete = ref(null);

const columns = [
  { title: "Alert name", dataIndex: "label", key: "label", width: 200 },
  { title: "Status", dataIndex: "enabled", key: "enabled", width: 120 },
  { title: "Project", dataIndex: "service_name", key: "service_name", width: 160 },
  { title: "Space", dataIndex: "space_name", key: "space_name", width: 160 },
  { title: "Team chat group", dataIndex: "channel_name", key: "channel_name", width: 160 },
  { title: "Created on", dataIndex: "created_at", key: "created_at", width: 180 },
  { title: "", key: "actions", width: 50 },
];

const tableData = computed(() =>
  alerts.value.map((alert) => {
    const service = services.value.find((s) => s.id === alert.service_id);
    const space = service?.space_id
      ? spaces.value.find((sp) => sp.id === service.space_id)
      : null;
    return {
      ...alert,
      service_name: service?.name || "-",
      space_name: space?.name || "-",
      channel_name: channelsMap.value[alert.config?.channel_id] || "-",
    };
  }),
);

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const fetchAll = async () => {
  loading.value = true;
  try {
    // Fetch spaces + services in parallel
    const [spacesRes, servicesRes] = await Promise.all([
      SpaceService.fetchSpacesWithRelationships({
        service_type: spaceServiceTypes.ERROR_LOGGING,
      }).catch(() => null),
      ErrorTrackingService.listServices().catch(() => null),
    ]);

    // Parse spaces
    if (spacesRes?.success && spacesRes?.data?.spaces) {
      spaces.value = spacesRes.data.spaces;
    }

    // Parse services - try direct API first, then extract from spaces
    const rawServices = servicesRes?.data || servicesRes;
    if (Array.isArray(rawServices) && rawServices.length > 0) {
      services.value = rawServices;
    } else if (spaces.value.length > 0) {
      const servicesArr = [];
      for (const space of spaces.value) {
        const rels = space.relationships
          ?.filter((rel) => rel.relation === spaceRelationTypes.ERROR_LOGGING_SERVICES)
          .map((rel) => ({ ...(rel.data || rel), space_id: space.id })) || [];
        servicesArr.push(...rels);
      }
      services.value = servicesArr;
    }

    // Fetch alerts for all services in parallel
    const alertResults = await Promise.all(
      services.value.map(async (service) => {
        try {
          const data = await ErrorTrackingService.listAlerts(service.id);
          const serviceAlerts = data?.data || data || [];
          return Array.isArray(serviceAlerts) ? serviceAlerts : [];
        } catch {
          return [];
        }
      }),
    );
    alerts.value = alertResults.flat();

    // Fetch channel names
    const channelIds = [...new Set(alerts.value.map((a) => a.config?.channel_id).filter(Boolean))];
    const channelPromises = channelIds.map(async (id) => {
      try {
        const res = await ChatService.fetchGroupByID(id);
        const group = res?.data || res;
        if (group?.name) channelsMap.value[id] = group.name;
      } catch {
        // skip
      }
    });
    await Promise.all(channelPromises);
  } catch (error) {
    console.error("Failed to fetch alerts:", error);
  } finally {
    loading.value = false;
  }
};

const openCreate = () => {
  router.push({ name: "error-logging-alert-create" });
};

const handleRowClick = (record) => {
  router.push({
    name: "error-logging-alert-detail",
    params: { alertId: record.id },
  });
};

const handleAction = async (key, record) => {
  if (key === "edit") {
    router.push({
      name: "error-logging-alert-detail",
      params: { alertId: record.id },
    });
  } else if (key === "test") {
    try {
      await ErrorTrackingService.testAlert(record.id);
      message.success("Test notification sent.");
    } catch {
      message.error("Failed to send test notification.");
    }
  } else if (key === "delete") {
    alertToDelete.value = record;
    showDeleteModal.value = true;
  }
};

const handleDelete = async () => {
  if (!alertToDelete.value) return;
  deleting.value = true;
  try {
    await ErrorTrackingService.deleteAlert(alertToDelete.value.id);
    message.success("Alert deleted.");
    showDeleteModal.value = false;
    alertToDelete.value = null;
    await fetchAll();
  } catch {
    message.error("Failed to delete alert.");
  } finally {
    deleting.value = false;
  }
};

onMounted(() => {
  fetchAll();
});
</script>
